import { Box, Stack } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { FormSideDrawer } from '@/modules/common/SideDrawer/FormSideDrawer';

import { ClientOrganizationForm } from './ClientOrganizationForm';
import {
  ClientOrganizationFormShape,
  defaultValues,
} from './ClientOrganizationModal.constants';
import { mapFormDataToInput } from './CreateClientOrganizationModal.utils';
import { useCreateClientOrganizationMutation } from './graphql/ClientOrganizationModal.generated';

export interface CreateClientOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  householdId: string;
  onAfterCreate?: (newId: string) => void;
  initialOrganizationName?: string;
  createdFromSuggestionID?: string;
}

interface CreateClientOrganizationModalInnerProps
  extends CreateClientOrganizationModalProps {
  heading?: string;
}

function CreateClientOrganizationModalInner({
  onClose,
  heading = 'Add an organization',
  householdId,
  onAfterCreate,
  createdFromSuggestionID,
}: CreateClientOrganizationModalInnerProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const trackUserEvent = useTrackUserEvent();
  const { formRef, handleSave } = useFormSaveHandler();
  const { handleSubmit, reset, shouldBlockNavigation, formState } =
    useFormContext<ClientOrganizationFormShape>();

  const [createMutation, { loading }] = useCreateClientOrganizationMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      showFeedback(`Created organization successfully`, {
        variant: 'success',
      });
      trackUserEvent('client_organization created');
      if (data.createClientOrganization) {
        onAfterCreate?.(data.createClientOrganization.id);
      }
    },
    onError: (error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError('Could not create client organization', error);
    },
  });

  const onSubmit = handleSubmit(async (formData) => {
    const output = await createMutation({
      variables: {
        input: mapFormDataToInput(
          formData,
          householdId,
          createdFromSuggestionID
        ),
      },
    });

    return output;
  });

  const closeDrawer = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(() => {
    closeDrawer();
  });

  return (
    <FormSideDrawer
      isOpen
      onClose={closeDrawer}
      heading={heading}
      shouldBlockClose={shouldBlockNavigation}
      data-testid="create-client-organization-modal"
      body={
        <form
          ref={formRef}
          onSubmit={onSubmit}
          noValidate
          style={{ width: '100%' }}
        >
          <Box mt={2}>
            <ClientOrganizationForm />
          </Box>
        </form>
      }
      footer={
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="secondary" size="sm" onClick={closeDrawer}>
            Cancel
          </Button>
          <Button
            data-testid="client-organization-modal-save"
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={loading || formState.isSubmitting}
          >
            Add organization
          </Button>
        </Stack>
      }
    />
  );
}

export function CreateClientOrganizationModal(
  props: CreateClientOrganizationModalProps
) {
  if (!props.isOpen) {
    return null;
  }

  return <CreateClientOrganizationModalMounted {...props} />;
}

function CreateClientOrganizationModalMounted({
  householdId,
  initialOrganizationName,
  createdFromSuggestionID,
  ...props
}: CreateClientOrganizationModalProps) {
  const formMethods = useForm<ClientOrganizationFormShape>({
    defaultValues: {
      ...defaultValues,
      name: initialOrganizationName || '',
    },
  });

  return (
    <FormProvider {...formMethods}>
      <CreateClientOrganizationModalInner
        householdId={householdId}
        initialOrganizationName={initialOrganizationName}
        createdFromSuggestionID={createdFromSuggestionID}
        {...props}
      />
    </FormProvider>
  );
}
